import {
  GSIS_PAYMENT_SUMMARY_FIELDS,
  HDMF_PREMIUM_REMITTANCE_FIELDS,
} from "../payrollReportConfig";
import {
  COOP_DEDUCTION_LOOKUPS,
  LOAN_DEDUCTION_LOOKUPS,
} from "./deductions";
import {
  GENERAL_PAYROLL_LOOKUPS,
  GENERAL_PAYROLL_ONLY_FIELDS,
  GENERAL_PAYROLL_SUMS,
} from "./generalPayroll";
import {
  GSIS_EMPLOYEE_SHARE_LOOKUPS,
  GSIS_LOAN_SUMS,
} from "./gsis";
import {
  HDMF_EMPLOYEE_SHARE_LOOKUPS,
  HDMF_LOAN_TOTAL_LOOKUPS,
} from "./hdmf";
import { MONTHLY_RATE_LOOKUPS } from "./monthlyRate";
import { OVERPAYMENT_LOOKUPS } from "./overpayment";
import { PHIC_EMPLOYEE_SHARE_LOOKUPS } from "./philhealth";
import {
  attachRegularAndBackPayDocs,
  sumRegularAndBackPay,
} from "./shared";
import { TAX_LOOKUPS } from "./tax";

export function getPayrollReportExportPipeline(
  where = {},
  fields = [],
  { remittanceKind = null, paymentSummaryKind = null } = {},
) {
  const needsMonthlyRate =
    fields.includes("monthlyRate") || fields.includes("longevityPay");
  const needsOverPayment = fields.includes("overPayment");
  const needsGsisPaymentSummary = paymentSummaryKind === "gsis";
  const needsHdmfPaymentSummary = paymentSummaryKind === "hdmf";
  const needsLoanDeduction =
    fields.includes("totalLoanDeduction") || needsGsisPaymentSummary;
  const needsCoopDeduction = fields.includes("totalCoopDeduction");
  const needsGsisRemittance = remittanceKind === "gsis";
  const needsPhilhealthRemittance = remittanceKind === "philhealth";
  const needsPhicRemittance = remittanceKind === "phic";
  const needsGsisEmployeeShare =
    needsGsisRemittance || needsGsisPaymentSummary;
  const needsPhicEmployeeShare =
    needsPhilhealthRemittance || needsPhicRemittance;
  const needsHdmfPremiumRemittance =
    HDMF_PREMIUM_REMITTANCE_FIELDS.some((field) => fields.includes(field)) ||
    needsHdmfPaymentSummary;
  const needsHdmfLoanTotal = needsHdmfPaymentSummary;
  const needsGeneralPayroll = GENERAL_PAYROLL_ONLY_FIELDS.some((field) =>
    fields.includes(field),
  );
  const needsTax = fields.includes("tax");
  const needsLongevity =
    fields.includes("longevityRate") || fields.includes("longevityPay");

  const pipeline = [];

  if (where && Object.keys(where).length > 0) {
    pipeline.push({ $match: where });
  }

  pipeline.push({ $sort: { createdAt: -1, surname: 1, Firstname: 1 } });

  pipeline.push(
    {
      $lookup: {
        from: "position_titles",
        localField: "position.id",
        foreignField: "_id",
        as: "positionLookup",
      },
    },
    {
      $lookup: {
        from: "employment_type",
        localField: "employmentType.id",
        foreignField: "_id",
        as: "employmentTypeLookup",
      },
    },
  );

  if (needsMonthlyRate) pipeline.push(...MONTHLY_RATE_LOOKUPS);
  if (needsOverPayment) pipeline.push(...OVERPAYMENT_LOOKUPS);
  if (needsLoanDeduction) pipeline.push(...LOAN_DEDUCTION_LOOKUPS);
  if (needsCoopDeduction) pipeline.push(...COOP_DEDUCTION_LOOKUPS);
  if (needsGsisEmployeeShare) pipeline.push(...GSIS_EMPLOYEE_SHARE_LOOKUPS);
  if (needsPhicEmployeeShare) pipeline.push(...PHIC_EMPLOYEE_SHARE_LOOKUPS);
  if (needsHdmfPremiumRemittance) pipeline.push(...HDMF_EMPLOYEE_SHARE_LOOKUPS);
  if (needsHdmfLoanTotal) pipeline.push(...HDMF_LOAN_TOTAL_LOOKUPS);
  if (needsGeneralPayroll) pipeline.push(...GENERAL_PAYROLL_LOOKUPS);
  if (needsTax && !needsGeneralPayroll) pipeline.push(...TAX_LOOKUPS);

  const addFields = {
    positionDoc: { $arrayElemAt: ["$positionLookup", 0] },
    employmentTypeDoc: { $arrayElemAt: ["$employmentTypeLookup", 0] },
  };

  if (needsMonthlyRate) {
    addFields.nonRegPositionDoc = { $arrayElemAt: ["$nonRegPositionLookup", 0] };
    addFields.salaryGradeDoc = { $arrayElemAt: ["$salaryGradeLookup", 0] };
  }

  if (needsOverPayment) {
    attachRegularAndBackPayDocs(addFields, "overpayment");
  }

  if (needsLoanDeduction) {
    attachRegularAndBackPayDocs(addFields, "loanDeduction");
  }

  if (needsCoopDeduction) {
    attachRegularAndBackPayDocs(addFields, "coopDeduction");
  }

  if (needsGsisEmployeeShare) {
    attachRegularAndBackPayDocs(addFields, "gsisEmployeeShare");
  }

  if (needsPhicEmployeeShare) {
    attachRegularAndBackPayDocs(addFields, "phicEmployeeShare");
  }

  if (needsHdmfPremiumRemittance) {
    attachRegularAndBackPayDocs(addFields, "hdmfEmployeeShare");
  }

  if (needsHdmfLoanTotal) {
    attachRegularAndBackPayDocs(addFields, "hdmfLoanTotal");
  }

  if (needsGeneralPayroll) {
    addFields.generalPayrollDoc = {
      $arrayElemAt: ["$generalPayrollLookup", 0],
    };
  }

  if (needsTax && !needsGeneralPayroll) {
    attachRegularAndBackPayDocs(addFields, "tax");
  }

  pipeline.push({ $addFields: addFields });

  const project = {
    _id: 0,
    id: { $ifNull: ["$id", "$_id"] },
    position: {
      $cond: {
        if: { $ne: ["$positionDoc", null] },
        then: {
          id: "$positionDoc._id",
          position_title: "$positionDoc.position_title",
        },
        else: "$position",
      },
    },
    employmentType: {
      $cond: {
        if: { $ne: ["$employmentTypeDoc", null] },
        then: {
          id: "$employmentTypeDoc._id",
          name: "$employmentTypeDoc.name",
        },
        else: "$employmentType",
      },
    },
  };

  const pipelineResolvedFields = new Set([
    "monthlyRate",
    "overPayment",
    "totalLoanDeduction",
    "totalCoopDeduction",
    "employeeShare",
    "employerShare",
    "totalGsis",
    "totalPremium",
    "employeePremium",
    "employerPremium",
    ...GSIS_PAYMENT_SUMMARY_FIELDS,
    ...HDMF_PREMIUM_REMITTANCE_FIELDS,
    ...Object.keys(GSIS_LOAN_SUMS),
    "tax",
    ...Object.keys(GENERAL_PAYROLL_SUMS),
    "longevityRate",
    "longevityPay",
  ]);

  // Plain employee columns (agency, surname, hired, ...) pass through as-is.
  fields.forEach((field) => {
    if (project[field] || pipelineResolvedFields.has(field)) return;
    project[field] = 1;
  });

  // Longevity is computed in JS from lengthOfService + monthlyRate.
  if (needsLongevity && !project.lengthOfService) {
    project.lengthOfService = 1;
  }

  if (needsMonthlyRate) {
    project.monthlyRate = {
      $ifNull: [
        "$nonRegPositionDoc.montlhyRate",
        { $ifNull: ["$salaryGradeDoc.Monthly Rate", ""] },
      ],
    };
  }

  if (needsOverPayment) {
    project.overPayment = sumRegularAndBackPay("overpayment", "overPayment");
  }

  if (needsLoanDeduction && fields.includes("totalLoanDeduction")) {
    project.totalLoanDeduction = sumRegularAndBackPay(
      "loanDeduction",
      "totalLoanDeduction",
    );
  }

  if (needsCoopDeduction) {
    project.totalCoopDeduction = sumRegularAndBackPay(
      "coopDeduction",
      "totalCoopDeduction",
    );
  }

  if (needsGsisRemittance) {
    const employeeShareTotal = sumRegularAndBackPay(
      "gsisEmployeeShare",
      "employeeShare",
    );
    project.employeeShare = employeeShareTotal;
    project.employerShare = "";
    project.totalGsis = employeeShareTotal;
  }

  if (needsPhilhealthRemittance) {
    const employeeShareTotal = sumRegularAndBackPay(
      "phicEmployeeShare",
      "employeeShare",
    );
    project.employeeShare = employeeShareTotal;
    project.employerShare = employeeShareTotal;
    project.totalPremium = { $multiply: [employeeShareTotal, 2] };
  }

  if (needsPhicRemittance) {
    const employeeShareTotal = sumRegularAndBackPay(
      "phicEmployeeShare",
      "employeeShare",
    );
    project.employeePremium = employeeShareTotal;
    project.employerPremium = employeeShareTotal;
    project.totalPremium = { $multiply: [employeeShareTotal, 2] };
  }

  if (needsGsisPaymentSummary) {
    const premiums = sumRegularAndBackPay(
      "gsisEmployeeShare",
      "employeeShare",
    );
    const loans = sumRegularAndBackPay(
      "loanDeduction",
      "totalLoanDeduction",
    );
    project.premiums = premiums;
    project.loans = loans;
    project.totalPayment = { $add: [premiums, loans] };
  }

  if (needsHdmfPaymentSummary) {
    const premiums = sumRegularAndBackPay(
      "hdmfEmployeeShare",
      "employeeContribution",
    );
    const loans = sumRegularAndBackPay("hdmfLoanTotal", "loans");
    project.premiums = premiums;
    project.loans = loans;
    project.totalPayment = { $add: [premiums, loans] };
  }

  if (needsHdmfPremiumRemittance && !needsHdmfPaymentSummary) {
    const employeeContribution = sumRegularAndBackPay(
      "hdmfEmployeeShare",
      "employeeContribution",
    );
    project.employeeContribution = employeeContribution;
    project.employerContribution = "";
    project.Total = employeeContribution;
  }

  if (needsGeneralPayroll) {
    Object.keys(GENERAL_PAYROLL_SUMS).forEach((field) => {
      if (!fields.includes(field)) return;
      project[field] = {
        $ifNull: [`$generalPayrollDoc.${field}`, 0],
      };
    });
  }

  if (needsTax && !needsGeneralPayroll) {
    project.tax = sumRegularAndBackPay("tax", "tax");
  }

  pipeline.push({ $project: project });

  return pipeline;
}
